import { 
  PresignedUrlRequest, 
  PresignedUrlResponse, 
  DownloadUrlRequest, 
  DownloadUrlResponse, 
  UploadConfigResponse 
} from '../types/uploads';

/**
 * Get a presigned URL for uploading a file to S3
 */
export const getPresignedUrl = async (
  callApi: (config: any) => Promise<any>,
  request: PresignedUrlRequest
): Promise<PresignedUrlResponse> => {
  const response = await callApi({
    method: 'POST',
    url: '/uploads/presigned-url',
    data: request,
  });
  
  return response.data;
};

/**
 * Get a signed download URL for a file from S3
 */
export const getDownloadUrl = async (
  callApi: (config: any) => Promise<any>,
  request: DownloadUrlRequest
): Promise<DownloadUrlResponse> => {
  const response = await callApi({
    method: 'POST',
    url: '/uploads/download-url',
    data: request,
  });
  
  return response.data;
};

/**
 * Delete a file from S3
 */
export const deleteFile = async (
  callApi: (config: any) => Promise<any>,
  key: string
): Promise<void> => {
  await callApi({
    method: 'DELETE',
    url: `/uploads/${encodeURIComponent(key)}`,
  });
};

/**
 * Get upload configuration (supported types, limits, etc.)
 */
export const getUploadConfig = async (
  callApi: (config: any) => Promise<any>
): Promise<UploadConfigResponse> => {
  const response = await callApi({
    method: 'GET',
    url: '/uploads/config',
  });
  
  return response.data;
};

/**
 * Upload a file directly to S3 using a presigned URL
 */
export const uploadToS3 = async (
  file: File,
  uploadUrl: string,
  onProgress?: (progress: { loaded: number; total: number; percentage: number }) => void
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    
    // Set up progress tracking
    if (onProgress) {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const percentage = Math.round((event.loaded / event.total) * 100);
          onProgress({
            loaded: event.loaded,
            total: event.total,
            percentage,
          });
        }
      });
    }
    
    // Handle completion
    xhr.addEventListener('load', () => {
      if (xhr.status === 200) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status: ${xhr.status}`));
      }
    });
    
    // Handle errors
    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed due to network error'));
    });
    
    xhr.addEventListener('abort', () => {
      reject(new Error('Upload was aborted'));
    });
    
    // Configure and send request
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', file.type);
    // Must include this header because it was part of the presigned signature
    // (see ServerSideEncryption: 'AES256' in backend S3Service)
    xhr.setRequestHeader('x-amz-server-side-encryption', 'AES256');
    xhr.send(file);
  });
}; 